import React, { useEffect, useState } from 'react'
import { Alert, Button, Empty, Input, Select, Space, Table, Tag, Typography } from 'antd'
import { menuAdminRequest, errorMessage } from './api'
import HelpHint from './HelpHint'

export const OBSERVATION_OUTCOMES = { GRANTED: '有菜单权限', WOULD_DENY: '正式拦截时会拒绝', UNKNOWN: '未能判断' }
const reasons = {
    MENU_GRANTED: '拥有至少一个有效菜单权限', NO_MENU_PERMISSION: '没有匹配菜单的有效权限',
    LOGIN_REQUIRED: '未取得已登录身份', UNMAPPED: '未找到菜单接口关系', UNCONFIGURED_SYSTEM: '关系尚未配置系统策略',
    AUTHORITY_UNAVAILABLE: '权限查询不可用', RELATION_STORE_UNAVAILABLE: '菜单接口关系查询不可用',
    AMBIGUOUS_PATH: '命中多个路径模板，归属存在歧义', INVALID_REQUEST: '请求方法、路径或用户身份格式无效',
}
// 2026-10-28 coder(lq): 后端Long按字符串输出；只在时间边界校验转换，记录ID/游标仍保留字符串，避免精度丢失。
function observationEpoch(value) {
    if (typeof value !== 'number' && !(typeof value === 'string' && /^[1-9][0-9]{0,15}$/.test(value))) return NaN
    const time = Number(value)
    return Number.isSafeInteger(time) && time > 0 && Number.isFinite(new Date(time + 8 * 3600000).getTime()) ? time : NaN
}
export function observationTime(value) {
    const time = observationEpoch(value)
    const date = new Date(time + 8 * 3600000)
    return Number.isFinite(time) ? date.toISOString().slice(0, 19).replace('T', ' ') : '—'
}
const colors = { GRANTED: 'green', WOULD_DENY: 'orange', UNKNOWN: 'default' }

// 2026-10-27 coder(lq): 观察页只读、手工刷新、有界游标分页；故障不伪装成无记录，不自动开启或保存任何策略。
export default function ObservationPanel() {
    const [draft, setDraft] = useState({ hours: 24 })
    const [query, setQuery] = useState({ hours: 24, size: 20 })
    const [history, setHistory] = useState([])
    const [data, setData] = useState(null)
    const [systems, setSystems] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    useEffect(() => {
        let active = true; setLoading(true); setError(''); setData(null)
        menuAdminRequest('observations', { params: query }).then(result => {
            const since = result && observationEpoch(result.since), until = result && observationEpoch(result.until)
            if (!result || !Array.isArray(result.items) || !Array.isArray(result.systems) || !result.collector
                || typeof result.hasMore !== 'boolean' || !Number.isFinite(since) || !Number.isFinite(until) || since > until
                || result.items.some(row => !row || typeof row.id !== 'string' || !/^[1-9][0-9]{0,18}$/.test(row.id)
                    || !Array.isArray(row.systems) || row.systems.some(item => !item || typeof item.title !== 'string')
                    || typeof row.path !== 'string' || typeof row.method !== 'string')
                || result.systems.some(item => !item || typeof item.key !== 'string' || typeof item.title !== 'string')
                || (result.hasMore && (!result.items.length || result.nextCursor !== result.items[result.items.length - 1].id))) {
                throw new Error('观察记录响应不完整，请确认配套后端已部署')
            }
            if (active) { setData({ ...result, since, until }); setSystems(result.systems) }
        }).catch(err => { if (active) setError(errorMessage(err)) })
            .finally(() => { if (active) setLoading(false) })
        return () => { active = false }
    }, [query])
    const search = () => { setHistory([]); setQuery({ ...draft, path: draft.path || undefined, size: 20 }) }
    const columns = [
        { title: '时间（北京时间）', dataIndex: 'createdAt', width: 175, render: observationTime },
        { title: '系统', dataIndex: 'systems', width: 160, render: values => (values || []).map(item => item.title).join('、') || '未知系统' },
        { title: '接口（脱敏模板）', dataIndex: 'path', width: 300, render: (path, row) => <div className="menu-api-path">
            <Tag>{row.method}</Tag>{path}{row.pathCount > 1 && <Typography.Text type="secondary">（共 {row.pathCount} 个匹配模板，仅展示首条）</Typography.Text>}</div> },
        { title: '观察结果', dataIndex: 'outcome', width: 190, render: value => <Tag color={colors[value]}>{OBSERVATION_OUTCOMES[value] || '未能判断'}</Tag> },
        { title: '原因', dataIndex: 'reason', width: 230, render: reason => reasons[reason] || `未识别原因（${reason || '空'}）` },
        { title: <>用户标识（脱敏）<HelpHint label="观察用户标识说明">只保存实例内加盐摘要，不保存账号、姓名或原始用户ID。用于对比同实例生命周期内的访问；不同实例或重启后不可直接关联。未登录显示“匿名”。</HelpHint></>,
            dataIndex: 'userKey', width: 220, render: value => <span className="menu-api-path">{value === 'ANONYMOUS' ? '匿名' : value === 'UNKNOWN' ? '未知' : value}</span> },
    ]
    const collector = data && data.collector
    const loss = collector && (collector.limited > 0 || collector.failed > 0 || collector.storeBackoff || collector.catalogUnavailable)
    return <section aria-label="权限观察记录">
        <Space className="menu-api-toolbar">
            <Select aria-label="观察时间范围" className="menu-api-select" value={draft.hours} onChange={hours => setDraft({ ...draft, hours })}
                options={[{ value: 1, label: '最近1小时' }, { value: 24, label: '最近24小时' }, { value: 168, label: '最近7天' }]} />
            <Select aria-label="观察系统" className="menu-api-select" allowClear placeholder="全部系统" value={draft.system}
                onChange={system => setDraft({ ...draft, system })} options={systems.map(item => ({ value: item.key, label: item.title }))} />
            <Select aria-label="观察结果筛选" className="menu-api-select" allowClear placeholder="全部结果" value={draft.outcome}
                onChange={outcome => setDraft({ ...draft, outcome })} options={Object.entries(OBSERVATION_OUTCOMES).map(([value, label]) => ({ value, label }))} />
            <Input aria-label="观察接口关键词" className="menu-api-observation-search" maxLength={128} placeholder="接口模板关键词"
                value={draft.path || ''} onChange={event => setDraft({ ...draft, path: event.target.value })} onPressEnter={search} />
            <Button aria-label="查询并刷新观察记录" type="primary" loading={loading} onClick={search}>查询／刷新记录</Button>
            <HelpHint label="观察记录说明">只展示服务端实际收到的AUDIT判定，不代表全量接口访问日志。现有网关主要上送已映射接口，关闭、豁免、未接入或未上送的接口不会因此有记录；没有记录不等于有权限或已覆盖。
                采用每实例每秒最多5条、按到达顺序保留的限量诊断，默认保留7天，异步写入可能延迟或丢失。未能判断不等于有权限。
                不保存请求正文、查询参数、登录凭据或实际动态路径值；没有模板时只显示脱敏服务路径，不能用它反推出完整未映射接口。歧义路径只展示首条模板，关键词也只搜索该脱敏模板。
                系统名称来自采集时的菜单层级快照，当前筛选选项来自最新菜单；历史已删除系统仍可在全部系统中查看。这里没有配置写入或自动开启拦截。</HelpHint>
        </Space>
        {error && <Alert type="error" showIcon message={error} action={<Button size="small" onClick={() => setQuery({ ...query })}>重试读取</Button>} />}
        {collector && <Space className="menu-api-observation-status">
            <Tag>仅观察记录</Tag><span>本页 {data.items.length} 条</span>
            {loss && <Tag color="orange">采集有缺口</Tag>}
            <HelpHint label="观察采集状态">以下计数仅属于本次响应实例 {collector.instance}，不是所有实例或当前筛选的统计：收到 {collector.received}，写入 {collector.written}，限流／队列丢弃 {collector.limited}，故障丢弃 {collector.failed}，待写入 {collector.queued}。
                最近写入：{observationTime(collector.lastWriteAt)}；最近存储异常：{observationTime(collector.lastFailureAt)}。
                {collector.storeBackoff ? '记录存储故障正在退避，不影响原权限判定。' : ''}{collector.catalogUnavailable ? '菜单层级读取失败，新记录可能显示为未知系统。' : ''}
                记录存在不证明请求已到达业务或业务执行成功；观察不替代原登录校验，也不代表正式拦截已验证。</HelpHint>
        </Space>}
        {!error && (data && !data.items.length && !loading
            ? <Empty description="当前条件没有已采集记录；可先核对已保存的观察模式，再正常操作并刷新" />
            : <Table rowKey="id" dataSource={data ? data.items : []} columns={columns} loading={loading}
            scroll={{ x: 1280 }} pagination={false} locale={{ emptyText: '当前条件没有已采集记录；可先核对已保存的观察模式，再正常操作并刷新' }} />)}
        <Space className="menu-api-toolbar"><Button disabled={loading || !data || !history.length} onClick={() => {
            setQuery({ ...query, beforeId: history[history.length - 1] }); setHistory(history.slice(0, -1))
        }}>上一页</Button><span>第 {history.length + 1} 页</span>
            <Button disabled={loading || !data || !data.hasMore} onClick={() => {
                setHistory([...history, query.beforeId]); setQuery({ ...query, until: data.until, beforeId: data.nextCursor })
            }}>下一页</Button>
            {data && <Typography.Text type="secondary">查询范围：{observationTime(data.since)} 至 {observationTime(data.until)}</Typography.Text>}
        </Space>
    </section>
}
